import { useState } from "react";

import { ComboField } from "@/components/kit";
import { useGetAllProducts } from "@/hooks/api/product/useGetAllProducts";
import { useGetProductSummary } from "@/hooks/api/product/useGetProductSummary";
import { useDebounce } from "@/hooks/useDebounce";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULT_LIMIT = 20;

interface BannerProductPickerProps {
  productId: string;
  errorMessage?: string;
  onBlur: () => void;
  onChange: (productId: string) => void;
}

export const BannerProductPicker = ({
  productId,
  errorMessage,
  onBlur,
  onChange,
}: BannerProductPickerProps) => {
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const debouncedSearchTerm = useDebounce(
    (searchTerm ?? "").trim(),
    SEARCH_DEBOUNCE_MS,
  );

  const { data } = useGetAllProducts({
    searchTerm: debouncedSearchTerm,
    limit: SEARCH_RESULT_LIMIT,
  });

  const { data: selectedProduct } = useGetProductSummary(productId);

  const products = data?.data ?? [];
  const selectedName =
    products.find((product) => product.id === productId)?.name ??
    selectedProduct?.name ??
    "";

  return (
    <ComboField
      disableFilter
      emptyText="No products found"
      errorMessage={errorMessage}
      inputValue={searchTerm ?? selectedName}
      isInvalid={!!errorMessage}
      items={products.map((product) => ({
        id: product.id,
        name: product.name,
      }))}
      label="Product"
      placeholder="Search products by name"
      value={productId}
      onBlur={onBlur}
      onChange={(id) => {
        setSearchTerm(null);
        onChange(id);
      }}
      onInputChange={setSearchTerm}
    />
  );
};
